<template>
	<section
		ref="inPageNavSection"
		:id="'in-page-section-id-' + item.slug"
		class="in-page-nav-section transitions-disabled"
		:class="[
			item.bg ? `bg--${item.bg}` : ``,
			{ 'in-page-nav-section--with-header': this.navHeader }
		]"
	>
		<a class="in-page-nav-section__anchor" :id="item.slug" />

		<a
			href="#"
			ref="accordionHeader"
			@click.prevent="accordionHeaderClickHandler(false)"
			:class="{
				'caret-indicator-white': caretIndicatorWhite,
				'caret-indicator-gray': caretIndicatorGray,
				'in-page-nav-section__header--active': accordionActive,
				'in-page-nav-section__header--fixed': fixedHeader,
				'in-page-nav-section__header--bottom': accordionStickToBottom
			}"
			class="in-page-nav-section__header"
		>
			<div class="container">
				<span class="header-text">{{ item.title }}</span>
				<span class="icon icon-plus" aria-hidden="true" />
			</div>
			<span
				v-if="caretIndicatorWhite || caretIndicatorGray"
				:class="[
					{ 'caret-indicator-white--right': caretIndicatorWhite },
					{ 'caret-indicator-gray--right': caretIndicatorGray }
				]"
			/>
		</a>

		<!--Dynamic component returned by item.component prop -->
		<transition-expand :show="isMobile">
			<div
				v-show="accordionActive || this.model.isMedium || this.model.isLarge"
				class="in-page-nav-section__body"
				ref="accordionBody"
			>
				<div
					class="in-page-nav-section__wrapper"
					:class="sectionWrapperCss(item)"
				>
					<component :is="sectionComponent" v-bind="sectionComponentProps" />
				</div>
			</div>
		</transition-expand>
		<v-breakpoint v-model="model" />
	</section>
</template>

<script>
let VueScrollTo = require("vue-scrollto");
import TransitionExpand from "../transitions/TransitionExpand";
// import Style from "@vg-faset/core/styles/export-js.scss";
const Style = require("@vg-faset/core/styles/export-js.scss");
import { VBreakpoint, Model, EventBus } from "@vg-faset/core";
import { adobeLaunch } from "vg-fas-adobe-launch";

export default {
	props: {
		/**
		 * `item` is an Object containing the following properties
		 *
		 * item.slug  {string}  this will populate the # in the url
		 * item.title {string} The text that shows up in the nav
		 * item.component {vue component} the component that load into the section
		 * item.componentProps {object} props for item.component
		 * item.bg {string}  applies a css class `bg--${item.bg}` so you can alter the bg if you need to
		 * item.noBottomPaddingSmall {boolean} removes default bottom padding on small when designs call against it
		 * item.noTopPaddingSmall {boolean} removes default top padding on small when designs call against it
		 * item.noTopPadding {boolean} removes padding above section at all BPs
		 * item.noBottomPadding {boolean} removes padding below section at all BPs
		 *
		 */
		item: Object,
		index: Number,
		navHeader: Object,
		urlHash: String,
		openFirstSection: Boolean,
		caretIndicatorWhite: Boolean,
		caretIndicatorGray: Boolean
	},
	data() {
		return {
			active: false,
			model: new Model(),
			urlPath: window.location.pathname,
			fixedHeader: false,
			accordionActive: false,
			accordionStickToBottom: false,
			openOnce: false
		};
	},
	components: {
		TransitionExpand,
		VBreakpoint,
		Model
	},
	methods: {
		/* Throttles function to check which section is active */
		handleScroll() {
			if (!window.requestAnimationFrame) {
				setTimeout(this.checkActive, 300);
				setTimeout(this.accordionSticky, 300);
			} else {
				window.requestAnimationFrame(() => this.checkActive());
				window.requestAnimationFrame(() => this.accordionSticky());
			}

			if (
				this.openOnce &&
				this.isMobile &&
				this.item.openFirstSection &&
				!this.urlHash
			) {
				if (window.scrollY >= this.$el.offsetTop) {
					this.openOnce = false;
					this.$emit(
						"computeAccordionOffset",
						parseInt(this.$refs.accordionBody.offsetHeight) +
							parseInt(Style.accordionHeaderHeight),
						this.index
					);
					this.replaceState(this.urlPath, `#${this.item.slug}`);
					EventBus.$emit("accordion:change");
				}
			}
		},
		getStickyNavOffset() {
			return document.getElementsByClassName("vgn-navBar-sticky")[0]
				? document.getElementsByClassName("vgn-navBar-sticky")[0].offsetHeight
				: 0;
		},
		/* Sends event to in-page nav with active item slug */
		checkActive() {
			if (this.model.isMedium || this.model.isLarge) {
				let offsetTop = this.$el.getBoundingClientRect().top;
				let offsetBottom = this.$el.getBoundingClientRect().bottom;
				if (
					offsetTop <= this.navHeightSticky + this.getStickyNavOffset() &&
					!this.active
				) {
					this.active = true;
					this.$emit("setActiveSection", this.item.slug);
				} else if (
					offsetBottom > this.navHeightSticky + this.getStickyNavOffset() &&
					this.active
				) {
					this.active = false;
					this.$emit("setActiveSection", this.item.slug);
				}
			}
		},
		/* Handle what happens to sticky mobile accordion header when scrolling */
		accordionSticky() {
			if (this.model.isSmall) {
				let offsetTop = this.$el.getBoundingClientRect().top;
				let offsetBottom =
					this.$el.getBoundingClientRect().bottom -
					parseInt(Style.accordionHeaderHeight);
				if (
					offsetTop > this.getStickyNavOffset() &&
					this.fixedHeader &&
					this.accordionActive
				) {
					this.fixedHeader = false;
				} else if (
					offsetTop <= this.getStickyNavOffset() &&
					!this.fixedHeader &&
					this.accordionActive
				) {
					this.fixedHeader = true;
				}
				if (
					offsetBottom < 0 &&
					this.fixedHeader &&
					this.accordionActive &&
					!this.accordionStickToBottom
				) {
					this.accordionStickToBottom = true;
				} else if (
					offsetBottom >= 0 &&
					this.fixedHeader &&
					this.accordionActive &&
					this.accordionStickToBottom
				) {
					this.accordionStickToBottom = false;
				}
			}
		},
		/* Sets active state on accordion headers  */
		accordionHeaderClickHandler(isUrlHash) {
			if (this.model.isSmall) {
				if (this.accordionActive === true) {
					this.replaceState(this.urlPath);
					this.accordionActive = false;
					this.$parent.accordionClosed = true;
					this.fixedHeader = false;
					VueScrollTo.scrollTo("#" + this.item.slug, {
						offset: this.getStickyNavOffset() * -1,
						cancelable: false,
						onDone: () => {
							this.fixedHeader = false;
						}
					});
				} else {
					this.$emit("closeAccordions");
					this.accordionActive = true;

					/* If clicked accordion comes after, offset = accordion offset, if same or before it = 0 */
					let accordionOffset;
					if (
						this.index > this.$parent.accordionLastIndex &&
						!this.$parent.accordionClosed
					) {
						accordionOffset =
							this.$parent.accordionOffset * -1 +
							parseInt(Style.accordionHeaderHeight) -
							this.getStickyNavOffset();
					} else {
						accordionOffset = this.getStickyNavOffset() * -1;
					}
					this.$parent.accordionClosed = false;
					/* Snap accordion to top */
					VueScrollTo.scrollTo("#" + this.item.slug, {
						offset: accordionOffset,
						cancelable: false,
						onDone: () => {
							this.fixedHeader = true;
							setTimeout(() => {
								if (isUrlHash) {
									adobeLaunch.trackSubpageViewed(this.item.title, "N"); // This should always happen after "pageLoadCompleted". The "onDone" event and the 1 second delay help prevent this. (see INSHUB-824)
								} else {
									adobeLaunch.trackSubpageViewed(this.item.title, "Y"); // This should always happen after "pageLoadCompleted". The "onDone" event and the 1 second delay help prevent this. (see INSHUB-824)
								}
							}, 1000);
						}
					});
					/*
            Use computed height to calculate offset of previously opened accordion,
            which is used as an offset for snapping the accordion header to the top
            when an earlier accordion is currently opened. Necessary for calculating
            height because the sections of a height of 0 at this time.
          */
					const { width } = getComputedStyle(this.$refs.accordionBody);
					this.$refs.accordionBody.width = width;
					this.$refs.accordionBody.style.position = "absolute";
					this.$refs.accordionBody.style.visibility = "hidden";
					this.$refs.accordionBody.style.display = "block";
					this.$refs.accordionBody.style.height = "auto";
					const { height } = getComputedStyle(this.$refs.accordionBody);
					this.$refs.accordionBody.width = "";
					this.$refs.accordionBody.style.position = "";
					this.$refs.accordionBody.style.visibility = "";
					this.$refs.accordionBody.style.display = "";
					this.$refs.accordionBody.style.height = 0;
					this.$emit(
						"computeAccordionOffset",
						parseInt(height) + parseInt(Style.accordionHeaderHeight),
						this.index
					);
					this.replaceState(this.urlPath, `#${this.item.slug}`);
					EventBus.$emit("accordion:change");
				}
			}
		},
		grabFocus() {
			let focusHeader = this.$el.querySelector(".focus-header");
			if (focusHeader) {
				focusHeader.focus();
			}
		},
		sectionWrapperCss(item) {
			return {
				"in-page-nav-section__wrapper--no-bottom-padding-sm":
					item.noBottomPaddingSmall,
				"in-page-nav-section__wrapper--no-top-padding-sm":
					item.noTopPaddingSmall,
				"in-page-nav-section__wrapper--no-top-padding": item.noTopPadding,
				"in-page-nav-section__wrapper--no-bottom-padding": item.noBottomPadding
			};
		},
		replaceState(path = "", hash = "") {
			history.replaceState({}, "", `${path}${window.location.search}${hash}`);
		}
	},
	/* Sets the dynamic component with the item.component prop */
	computed: {
		navHeightSticky() {
			if (this.navHeader) {
				return parseInt(Style.navHeightSticky);
			} else {
				return parseInt(Style.navHeightSticky / 2);
			}
		},
		sectionComponent() {
			return this.item.component;
		},
		sectionComponentProps() {
			return this.item.componentProps ? this.item.componentProps : {};
		},
		isMobile() {
			return this.model.isSmall;
		}
	},
	watch: {
		fixedHeader(newVal) {
			if (newVal === true) {
				this.$refs.accordionHeader.style.paddingTop =
					this.getStickyNavOffset() + "px";
			} else {
				this.$refs.accordionHeader.style.paddingTop = 0;
			}
		},
		isMobile(response) {
			if (response) {
				if (this.urlHash) {
					if (this.item.slug === this.urlHash) {
						this.accordionHeaderClickHandler(true);
					}
				} else {
					if (this.item.openFirstSection) {
						this.openOnce = true;
						this.accordionActive = true;
						this.$parent.accordionClosed = false;
					}
				}
			}
		}
	},
	/* Binds to scroll event*/
	created() {
		window.addEventListener("scroll", this.handleScroll);
	},
	mounted() {
		window.addEventListener("load", () => {
			this.$emit("countSectionLoaded");
			if (
				this.$refs.inPageNavSection &&
				this.$refs.inPageNavSection.classList
			) {
				this.$refs.inPageNavSection.classList.remove("transitions-disabled");
			}
			if (window.location.hash) {
				if (window.location.hash.substring(1) === this.item.slug) {
					this.accordionHeaderClickHandler(true);
				}
			}
			// This helps IE highlight the correct nav item on load.
			// Would be good to find a better fix if there's time.
			setTimeout(this.checkActive, 1000);
		});
	},
	updated() {
		// Emit this item's slug to InPageNav.vue when section updates to determine
		// onPageLoad navigation
		if (this.model.isMedium || this.model.isLarge) {
			this.$emit("inPageNavSectionUpdated", this.item.slug);
		}
	},
	destroyed() {
		window.removeEventListener("scroll", this.handleScroll);
	}
};
</script>

<style lang="scss" scoped>
/* These prevent the page scrolling to the bottom on page load */
// Prevent transition on small until page has loaded.
.transitions-disabled {
	.expand-enter-active,
	.expand-leave-active {
		overflow: visible;
		transition: none;
	}
	.expand-enter,
	.expand-leave-to {
		height: auto;
	}
}
// Always prevent transition on sections for medium/large
@include from-md() {
	.expand-enter-active,
	.expand-leave-active {
		overflow: visible;
		transition: none;
	}
	.expand-enter,
	.expand-leave-to {
		height: auto;
	}
}
</style>

<style lang="scss">
.in-page-nav-section {
	position: relative;
	&__wrapper {
		min-height: 200px;
		padding: 40px 0;
		@include from-md {
			padding: 56px 0;
		}
		@include from-lg {
			padding: 80px 0;
		}
		&--no-top-padding {
			padding-top: 0;
		}
		&--no-bottom-padding {
			padding-bottom: 0;
		}
		&--no-bottom-padding-sm {
			@include to-md {
				padding-bottom: 0;
			}
		}
		&--no-top-padding-sm {
			@include to-md {
				padding-top: 0;
			}
		}
	}
	@include from-md {
		&__anchor {
			display: block;
			position: relative;
			top: $nav-height-sticky / -2 + 1;
			visibility: hidden;
		}
		&--with-header {
			.in-page-nav-section__anchor {
				top: $nav-height-sticky * -1 + 1;
			}
		}
	}
	/* Mobile Accordion Styles */
	&__header {
		@include h6;
		background-color: $color--almost-black;
		border-bottom: 2px solid $color--black;
		color: $color--white;
		cursor: pointer;
		display: block;
		position: relative;
		&::before {
			border-left: 0 solid $color--mid-gray;
			content: "";
			height: 100%;
			position: absolute;
			transition: border-left-width ease 0.2s;
			top: 0;
		}
		&:hover {
			text-decoration: none;
			.header-text {
				text-decoration: underline;
			}
		}
		.icon-plus {
			transition: transform ease 0.2s;
		}
		&--active {
			&::before {
				border-left-width: 6px;
			}
			.icon-plus {
				transform: rotate(45deg);
			}
		}
		&--fixed {
			position: fixed;
			top: 0;
			width: 100%;
			z-index: 5;
			+ .in-page-nav-section__body {
				@include to-md {
					padding-top: $accordion-header-height;
				}
			}
		}
		&--bottom {
			bottom: 0;
			position: absolute;
			top: auto;
		}
		.container {
			align-items: center;
			display: flex;
			height: $accordion-header-height;
			justify-content: space-between;
		}
		@include from-md {
			display: none;
		}
	}

	&__header--active {
		&.caret-indicator-white {
			&::before {
				border-left-color: $color--almost-black;
			}

			$size: 12px;
			.caret-indicator-white--right {
				position: absolute;
				width: 0;
				height: 0;
				top: 50%;
				left: 0;
				transform: translate(0, -50%);
				border-top: $size solid transparent;
				border-bottom: $size solid transparent;
				border-left: $size solid $color--white;
			}
		}

		&.caret-indicator-gray {
			$size: 12px;
			.caret-indicator-gray--right {
				position: absolute;
				width: 0;
				height: 0;
				top: 50%;
				left: 4px;
				transform: translate(0, -50%);
				border-top: $size solid transparent;
				border-bottom: $size solid transparent;
				border-left: $size solid $color--mid-gray;
			}
		}
	}
}
</style>
